import * as React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BaseComponent } from '../../../prop-types/component';
import useTranslation from '../../../components/customHooks/translations';
import { useSignupIntendUse } from '../../../hooks/use-signup-intend-use.hook';
import { SIGNUP_NAMES, SIGNUP_PATHS } from '../../../constants/signup-paths';
import { LoadingSpinner } from '../LoadingSpinner';
import { ResponseError } from '../ResponseError';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { visuallyHidden } from '@mui/utils';
import { useAvatar } from '../../../hooks/use-get-avatar.hook';

export const UsageIntend = () => {
  const navigate = useNavigate();

  const { signup } = useTranslation();
  const { avatar, isLoading } = useAvatar();

  const { intendUseOptions, setIntendUse, sendIntendUseRequest } =
    useSignupIntendUse();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selectedItems, setSelectedItems] = React.useState([]);

  React.useEffect(() => {
    setIntendUse(selectedItems);
  }, [selectedItems, setIntendUse]);

  const handleSelection = (itemId) => {
    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.includes(itemId)
        ? prevSelectedItems.filter((id) => id !== itemId)
        : [...prevSelectedItems, itemId]
    );
  };

  const onSubmitForm = async (event) => {
    event.preventDefault();

    if (!loading) {
      setLoading(true);
      setError(null);

      try {
        await sendIntendUseRequest();
        setLoading(false);
        navigate(SIGNUP_PATHS.USAGE_INSTRUCT);
      } catch (e) {
        setLoading(false);
        setError(e?.message || 'Something went wrong. Please try again.');
        console.log('Error: ', e);
      }
    }
  };
  return (
    <Box data-testid="usage-intend" sx={{ maxWidth: '100%' }}>
      {!isLoading && (
        <Box
          component="img"
          alt=""
          src={avatar.phone || avatar.imageUrl}
          sx={{
            mb: '1em',
            width: '10%',
            height: '10%',
            minWidth: '8em',
            maxWidth: '100%',
          }}
        />
      )}
      <Typography variant="h1" id="usage-intend-label">
        {signup.usageIntendHeading}
      </Typography>
      <Box
        id={`form-${SIGNUP_NAMES.USAGE_INTEND}`}
        component="form"
        onSubmit={onSubmitForm}
        sx={{
          maxWidth: '35rem',
          margin: '2em auto',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '1rem',
        }}
      >
        <FormGroup
          aria-labelledby="usage-intend-label"
          sx={{ alignItems: 'center', gap: '0.8em', paddingRight: '2em' }}
        >
          {intendUseOptions.map((option) => {
            const isChecked = selectedItems.includes(option.id);
            return (
              <FormControlLabel
                key={option.id}
                value={option.id}
                variant="lightPill"
                className={isChecked ? 'checked' : ''}
                disabled={loading}
                control={
                  <Checkbox
                    value={option.id}
                    icon={<CheckCircleIcon sx={visuallyHidden} />}
                    checkedIcon={<CheckCircleIcon sx={{ color: 'black' }} />}
                    checked={isChecked}
                    onChange={() => handleSelection(option.id)}
                    disabled={loading}
                  />
                }
                label={
                  <Typography display="inline" sx={{ textAlign: 'left' }}>
                    {option.label}
                  </Typography>
                }
                labelPlacement="start"
                sx={{
                  width: '100%',
                  margin: 0,
                  justifyContent: 'space-between',
                  '& .MuiFormControlLabel-label.Mui-disabled': {
                    color: '#555',
                  },
                }}
              />
            );
          })}
        </FormGroup>
      </Box>
      <LoadingSpinner loading={loading} />
      <ResponseError error={error} />
    </Box>
  );
};

UsageIntend.propTypes = {
  ...BaseComponent,
};
